import { types, getParentOfType, hasParent, flow } from 'mobx-state-tree';
import Validator from 'validatorjs';
import en from 'validatorjs/src/lang/en';
import { getp } from '../../helpers/getProp';
import {
  mutate,
  query,
} from '../../core/createApolloClient/createApolloClient.client';
import mutateAddress from './mutateAddressM';
import getAddressQ from './getAddressQ';
import _ from 'lodash';
import { toJS } from 'mobx';

Validator.setMessages('en', en);

const stringModel = types
  .model('StringModel', {
    value: types.maybeNull(types.string),
    touched: false,
    rules: types.array(types.string),
  })
  .actions(self => {
    return {
      touch() {
        self.touched = true;
      },
      setRules(rules) {
        self.rules = rules;
      },
      set(value) {
        self.value = value;
      },
    };
  })
  .views(self => {
    return {
      get validation() {
        let validation = new Validator(
          { value: self.value },
          { value: self.rules },
          { required: '*required', min: 'too short' },
        );
        return {
          isValid: validation.passes(),
          errors: validation.errors,
        };
      },
      get error() {
        if (!self.touched) return '';
        return getp(self.validation, 'errors.errors.value.0');
      },
      get isValid() {
        if (!self.touched) return '';
        return self.validation.isValid;
      },
    };
  });

export const addressModel = types
  .model('AddressModel', {
    id: types.maybeNull(types.identifierNumber),
    city: types.optional(stringModel, () =>
      stringModel.create({ rules: ['required', 'min:3'] }),
    ),
    state: types.optional(stringModel, () =>
      stringModel.create({
        value: 'UT',
        touched: true,
        rules: ['required', 'min:2', 'regex:/^[a-zA-Zss]*$/'],
      }),
    ),
    street: types.optional(stringModel, () =>
      stringModel.create({ rules: ['required', 'min:3'] }),
    ),
    buildingName: types.optional(stringModel, () => stringModel.create({})),
    zipcode: types.optional(stringModel, () =>
      stringModel.create({
        rules: [
          'required',
          'min:5',
          'max:10',
          'regex:/^[0-9]{5}(?:-[0-9]{4})?$/',
        ],
      }),
    ),
    label: types.optional(stringModel, () => stringModel.create({ rules: [] })),
    selected: false,
  })
  .actions(self => ({
    updateLabel(label) {
      self.label = label;
    },
    save: flow(function*() {
      self.city.touch();
      self.state.touch();
      self.street.touch();
      self.buildingName.touch();
      self.zipcode.touch();

      if (self.isValid) {
        if (hasParent(self)) {
          const parent = getParentOfType(self, AddressesType);
          yield parent.finishAdd();
        }
      }
    }),
    cancelAdd() {
      if (hasParent(self)) {
        const parent = getParentOfType(self, AddressesType);
        parent.cancelAdd();
      }
    },
    toggleSelected() {
      self.selected = !self.selected;
    },
  }))
  .views(self => {
    return {
      get isValid() {
        return (
          self.city.validation.isValid &&
          self.state.validation.isValid &&
          self.street.validation.isValid &&
          self.buildingName.validation.isValid &&
          self.zipcode.validation.isValid
        );
      },
      get addrStr1() {
        return `${self.street.value} ${self.buildingName.value || ''}`;
      },
      get addrStr2() {
        return `${self.city.value}, ${self.state.value} ${self.zipcode.value}`;
      },
    };
  });

const AddressesType = types
  .model('AddressesType', {
    selectedAddresses: types.optional(
      types.array(types.reference(addressModel)),
      [],
    ),
    addresses: types.optional(types.array(addressModel), []),
    adding: false,
    newAddress: types.maybeNull(addressModel),
    editing: false,
    editAddress: types.maybeNull(types.reference(addressModel)),
  })
  .actions(self => {
    return {
      afterCreate() {
        self.getAddresses();
      },
      getAddresses: flow(function*() {
        const res = yield query(getAddressQ);
        const addrs = getp(res, 'data.userAddresses', []);
        self.addresses = addrs.map(x => {
          const addr = addressModel.create({ id: x.id });
          addr.city.set(x.city);
          addr.state.set(x.state);
          addr.street.set(x.street);
          addr.zipcode.set(x.zipcode);
          addr.buildingName.set(x.buildingName);
          addr.label.set(x.label);
          return addr;
        });
      }),
      selectAddress(addr, single) {
        if (addr.id === 0) {
          return;
        }
        if (addr.selected) {
          if (self.selectedAddresses.length === 1) {
            self.selectedAddresses[0].toggleSelected();
            self.selectedAddresses = [];
          } else {
            self.selectedAddresses = self.selectedAddresses.filter(a => {
              const match = a && a.id === addr.id && a;

              if (match) {
                a.toggleSelected();
              }
              return !match;
            });
          }
        } else {
          if (single) {
            self.selectedAddresses.forEach(a => a.toggleSelected());
            self.selectedAddresses = [addr];
          } else {
            self.selectedAddresses.push(addr);
          }
          addr.toggleSelected();
        }
      },
      cancelAdd() {
        self.newAddress = null;
        self.editAddress = null;
        self.adding = false;
        self.editing = false;
      },
      edit(item) {
        self.editing = true;
        self.editAddress = item;
      },
      startAdd() {
        self.adding = true;
        self.newAddress = addressModel.create({ id: 0 });
      },
      initialSelection(selected) {
        _.forEach(selected, dbItem => {
          const idx = self.addressIdxLookup[dbItem.addressId];
          if (idx !== undefined && idx !== null) {
            self.selectAddress(self.addresses[idx]);
          }
        });
      },
      finishAdd: flow(function*() {
        const addr = self.focusedAddress;
        const res = yield mutate(mutateAddress, {
          city: addr.city.value,
          state: addr.state.value,
          street: addr.street.value,
          zipcode: addr.zipcode.value,
          buildingName: addr.buildingName.value,
          label: addr.label.value,
          id: (addr.id && addr.id >= 1) || undefined,
        });
        const addressSaved = getp(res, 'data.mutateAddress');
        if (addressSaved) {
          if (self.adding) {
            const addr = addressModel.create({ id: addressSaved.id });
            addr.city.set(addressSaved.city);
            addr.state.set(addressSaved.state);
            addr.street.set(addressSaved.street);
            addr.zipcode.set(addressSaved.zipcode);
            addr.buildingName.set(addressSaved.buildingName);
            addr.label.set(addressSaved.label);
            self.addresses.push(addr);
          }

          self.newAddress = null;
          self.editAddress = null;
          self.adding = false;
          self.editing = false;
        } else {
          console.log('Error saving address ', res);
        }
      }),
    };
  })
  .views(self => {
    return {
      get modifying() {
        return self.adding || self.editing;
      },
      get addressIdxLookup() {
        const lookup = {};
        self.addresses.forEach((a, i) => (lookup[a.id] = i));
        return lookup;
      },
      get focusedAddress() {
        return (
          (self.adding && self.newAddress) ||
          (self.editing && self.editAddress) ||
          null
        );
      },
      get selectedAddressesJS() {
        const simple = toJS(self.selectedAddresses);
        return simple
          .filter(s => s)
          .map(s => {
            return {
              buildingName: s.buildingName.value,
              city: s.city.value,
              state: s.state.value,
              street: s.street.value,
              zipcode: s.zipcode.value,
              label: s.label.value,
              id: s.id,
            };
          });
      },
    };
  });

export default AddressesType;



// WEBPACK FOOTER //
// ./src/components_new/addresses/AddressStore.js